import { memo } from 'react';
import { Badge } from '../../../atoms/Badge/Badge';
import { Card } from '../../../molecules/Card/Card';
import type { ChartSeriesToken } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { KPIText } from '../../../molecules/KPIText/KPIText';
import { StreamingText } from '../../../molecules/StreamingText/StreamingText';
import { TableHeader } from '../../../molecules/TableHeader/TableHeader';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { DonutChart } from '../../../organisms/DonutChart/DonutChart';
import { Table, type TableColumn, type TableRowData } from '../../../organisms/Table/Table';
import { token } from '../../../tokens/tokens';
import { SENTIMENT_BADGE, countFormat } from '../../tables/shared/tableShared';
import { PLOT_HEIGHT } from '../../widgets/shared/cardShell';
import styles from './BreakdownBody.module.css';

/**
 * The five widgets both breakdown panels draw, and the only thing they share.
 *
 * Figma has this content twice — `Drawer / Property 1=Breakdown` (2038:27494),
 * docked right at 598x876, and `Modal / Type=Breakdown` (2235:120695), centred
 * — with **identical** slots: three `Size=sm` cards, an `AI summary` card at
 * 148, `Share of voice` 380, `Top communities by mention` 380, and a 256
 * table. Only the container and its header differ, so the container is what
 * `PostBreakdownDrawer` and `ConversationsModal` each own.
 *
 * **The frame is a layout, not content.** The placeholders were read as saying
 * which *kind* of widget goes where, settled with the user 2026-09-18:
 *
 *   three `Size=sm` cards        →  Total comments · Relevant comments · Total shares
 *   AI summary                   →  the post's stored AI summary, else a sentence of the panel's own figures
 *   Share of voice (a donut)     →  Sentiment breakdown, as a donut
 *   Top communities (bars)       →  Reaction breakdown, as a horizontal bar chart
 *   the table                    →  Top comments
 */

/** The reaction chart's label gutter — fitted to "Celebrate", the widest name. Measured, not guessed. */
const REACTION_GUTTER = 72;

export type BreakdownCommentView = {
  id: string;
  text: string;
  /** Formatted. */
  reactions: string;
  sentiment: string;
  sentimentLabel: string;
};

/** One post's breakdown, ready to draw. */
export type BreakdownView = {
  /** The three tiles, formatted. */
  totalComments: string;
  relevantComments: string;
  totalShares: string;
  /** The AI summary paragraph (markdown `**bold**`). */
  summary: string;
  /**
   * Whether `summary` is the post's own stored AI summary. False when it has
   * none and `summary` is a sentence of the panel's figures instead.
   */
  summaryFromPost: boolean;
  /** False when the post has no relevant comments — the sentiment card and table are empty. */
  hasComments: boolean;
  /** One row per sentiment, in the chips' order: `{ x: label, comments }`. */
  sentimentRows: { x: string; comments: number }[];
  sentimentColors: ChartSeriesToken[];
  sentimentLegend: { label: string; color: ChartSeriesToken }[];
  /** Net sentiment and Negative share — unbadged on purpose (a badge reads as good or bad news). */
  sentimentKpis: KPIMetric[];
  /** The donut's centre figure: the positive share. */
  positiveShare: string;
  /** False when the post has no reactions. */
  hasReactions: boolean;
  /** One bar per reaction type, in the platform's own order. */
  reactionRows: { x: string; count: number }[];
  /** Total reactions and Top reaction — unbadged, for the reason above. */
  reactionKpis: KPIMetric[];
  topComments: BreakdownCommentView[];
};

export type BreakdownBodyProps = {
  view: BreakdownView;
  /**
   * What the help text calls the thing being broken down. The drawer is opened
   * from a table of posts and the modal from a table of conversations.
   */
  noun?: 'post' | 'conversation';
};

export const BreakdownBody = memo(function BreakdownBody({
  view,
  noun = 'post',
}: BreakdownBodyProps) {
  const columns: TableColumn[] = [
    { key: 'comment', header: 'Comment', width: '64%' },
    { key: 'reactions', header: 'Reactions', width: '18%' },
    { key: 'sentiment', header: 'Sentiment', cellType: 'badge', width: '18%' },
  ];
  const commentRows: TableRowData[] = view.topComments.map((c) => ({
    key: c.id,
    cells: {
      comment: { text: c.text },
      reactions: { text: c.reactions },
      sentiment: {
        content: <Badge type={SENTIMENT_BADGE[c.sentiment] ?? 'gray'}>{c.sentimentLabel}</Badge>,
      },
    },
  }));

  return (
    <div className={styles.content}>
      {/* Figma `Frame 2` — three `Size=sm` cards, `space/20` apart. */}
      <div className={styles.tiles}>
        <Card
          size="sm"
          title="Total comments"
          helpText={`Every comment on this ${noun}, relevant or not.`}
          kpi={<KPIText>{view.totalComments}</KPIText>}
        />
        <Card
          size="sm"
          title="Relevant comments"
          helpText={`Comments about the ${noun}'s subject, leaving out spam, tags and off-topic replies.`}
          helpSupportingText="The sentiment breakdown and top comments below are drawn from these."
          kpi={<KPIText>{view.relevantComments}</KPIText>}
        />
        <Card
          size="sm"
          title="Total shares"
          helpText={`How many times this ${noun} was shared.`}
          kpi={<KPIText>{view.totalShares}</KPIText>}
        />
      </div>

      {/*
        Figma's second card (2038:29327 in the drawer) — `AI summary`, 148 tall.
        It draws exactly two visible layers: the title, and one paragraph in a
        layer named `Streaming Text` — the molecule at its plainest. Not the AI
        summary feature that went out of scope (plan.md §7 item 358).
      */}
      <Card
        title="AI summary"
        helpText={
          view.summaryFromPost
            ? `An AI-written summary of this ${noun} and the conversation under it.`
            : `What this panel's own figures say about the ${noun}.`
        }
        helpSupportingText={
          view.summaryFromPost
            ? `Written when the ${noun} was analysed. The figures in this panel are counted live.`
            : `This ${noun} has no AI summary yet, so this states figures and never causes — nothing here knows why a comment was written.`
        }
        /* `fill`: Figma draws `Chart 1` at the card's full 542, past the molecule's 520 ceiling. */
        chart={<StreamingText fill text={view.summary} />}
      />

      <Card
        state={view.hasComments ? 'default' : 'empty'}
        emptyLabel={`No relevant comments on this ${noun}`}
        title="Sentiment breakdown"
        helpText={`How the relevant comments on this ${noun} split between positive, neutral and negative.`}
        kpi={<KPI metrics={view.sentimentKpis} />}
        chart={
          <DonutChart
            data={view.sentimentRows}
            nameKey="x"
            valueKey="comments"
            height={PLOT_HEIGHT}
            legend="none"
            centerLabel="positive"
            centerValue={view.positiveShare}
            colors={view.sentimentColors}
            formatValue={(v) => countFormat.format(v)}
          />
        }
        legend={
          <ChartLegend
            items={view.sentimentLegend.map((item) => ({
              label: item.label,
              color: token[item.color],
            }))}
          />
        }
      />

      <Card
        state={view.hasReactions ? 'default' : 'empty'}
        emptyLabel={`No reactions on this ${noun}`}
        title="Reaction breakdown"
        helpText={`This ${noun}'s reactions by type, in the platform's own set.`}
        helpSupportingText="Each platform offers its own reactions — Facebook has seven, LinkedIn six, and the rest a single like or upvote — so a post from those shows one bar."
        kpi={<KPI metrics={view.reactionKpis} />}
        chart={
          <BarChart
            data={view.reactionRows}
            xKey="x"
            series={[{ key: 'count', label: 'Reactions' }]}
            orientation="horizontal"
            /* Fitted to the longest reaction name — rules.md §4: size the gutter to the labels a chart draws. */
            categoryAxisWidth={REACTION_GUTTER}
            height={PLOT_HEIGHT}
            legend="none"
            formatValue={(v) => countFormat.format(v)}
          />
        }
        /* No legend: one series, and the category axis names every bar. */
      />

      <Table
        label="Top comments"
        columns={columns}
        rows={commentRows}
        emptyLabel={`No relevant comments on this ${noun}`}
        header={
          <TableHeader
            title="Top comments"
            helpText={`The most engaging relevant comments on this ${noun}, ordered by their reactions.`}
          />
        }
      />
    </div>
  );
});
